'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as Toast from '@radix-ui/react-toast';
import { syncPendingOperations } from '@/lib/offlineApi';
import { useAppStore } from '@/stores/appStore';

type ToastVariant = 'success' | 'error' | 'info';

type ShowToastArg =
  | string
  | {
      title: string;
      description?: string;
      variant?: ToastVariant;
      duration?: number;
    };

interface ToastContextValue {
  showToast: (arg: ShowToastArg) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToasterProvider>');
  return ctx;
}

// Connection monitor component that shows toast on offline/online
const ConnectionMonitor: React.FC<{ showToast: (arg: ShowToastArg) => void }> = ({ showToast }) => {
  const wasOfflineRef = useRef(false);
  const isSyncingRef = useRef(false);
  const setIsOffline = useAppStore((state) => state.setIsOffline);
  const setPendingOperationsCount = useAppStore((state) => state.setPendingOperationsCount);
  
  useEffect(() => {
    const handleOffline = () => {
      wasOfflineRef.current = true;
      setIsOffline(true);
      showToast({
        title: 'Offline Mode Active',
        description: 'Your changes will be saved locally and synced when you reconnect.',
        variant: 'info',
        duration: 5000
      });
    };
    
    const handleOnline = async () => {
      if (wasOfflineRef.current) {
        wasOfflineRef.current = false;
        setIsOffline(false);
        
        // Trigger sync of pending operations
        if (!isSyncingRef.current) {
          isSyncingRef.current = true;
          
          showToast({
            title: 'Connection Restored - Syncing...',
            description: 'Syncing your offline changes to the server.',
            variant: 'info',
            duration: 3000
          });

          try {
            const result = await syncPendingOperations((current, total, op) => {
              // Progress tracking
            });

            if (result.total > 0) {
              if (result.failed === 0) {
                showToast({
                  title: 'Sync Complete',
                  description: `Successfully synced ${result.success} operation(s).`,
                  variant: 'success',
                  duration: 3000
                });
              } else {
                showToast({
                  title: 'Sync Partially Complete',
                  description: `Synced ${result.success} operation(s). ${result.failed} failed.`,
                  variant: 'error',
                  duration: 5000
                });
              }
            }

            setPendingOperationsCount(0);
          } catch (error) {
            showToast({
              title: 'Sync Failed',
              description: 'Failed to sync offline changes. Will retry automatically.',
              variant: 'error',
              duration: 5000
            });
          } finally {
            isSyncingRef.current = false;
          }
        }
      }
    };
    
    // Check initial state
    if (typeof window !== 'undefined' && !navigator.onLine) {
      handleOffline();
    }
    
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [showToast, setIsOffline, setPendingOperationsCount]);
  
  return null;
};

export const ToasterProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [variant, setVariant] = useState<ToastVariant>('info');
  const [duration, setDuration] = useState<number>(3000);

  const showToast = useCallback((arg: ShowToastArg) => {
    if (typeof arg === 'string') {
      setTitle(arg);
      setDescription(undefined);
      setVariant('info');
      setDuration(3000);
    } else {
      setTitle(arg.title);
      setDescription(arg.description);
      setVariant(arg.variant ?? 'info');
      setDuration(arg.duration ?? 3000);
    }
    setOpen(false); // reset to retrigger
    requestAnimationFrame(() => setOpen(true));
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  const colorClasses = useMemo(() => {
    switch (variant) {
      case 'success':
        return 'bg-green-50 border-green-500 text-green-800';
      case 'error':
        return 'bg-red-600 text-white border-0';
      default:
        return 'bg-orange-50 border-orange-500 text-orange-800';
    }
  }, [variant]);

  return (
    <ToastContext.Provider value={value}>
      <Toast.Provider swipeDirection="right" duration={duration}>
        <ConnectionMonitor showToast={showToast} />
        {children}
        <Toast.Root
          open={open}
          onOpenChange={setOpen}
          className={`fixed right-2 top-2 z-[100] w-[360px] max-w-[90vw] rounded-md shadow-lg ${colorClasses}`}
        >
          <div className="p-4">
            <Toast.Title className="font-medium">{title}</Toast.Title>
            {description ? (
              <Toast.Description className="mt-1 text-sm opacity-90">
                {description}
              </Toast.Description>
            ) : null}
          </div>
          <Toast.Close className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded hover:bg-black/5 focus:outline-none" aria-label="Close">
            ✕
          </Toast.Close>
        </Toast.Root>
        <Toast.Viewport className="fixed right-0 top-0 z-[100] m-0 flex max-h-screen w-[420px] list-none flex-col gap-2 p-4 outline-none" />
      </Toast.Provider>
    </ToastContext.Provider>
  );
};
